import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { useTheme } from 'styled-components';
import { X, ChevronDown, Search } from 'lucide-react';
import type { ComboItem } from '@kardume/types';

interface SearchableComboBoxProps {
  items: ComboItem[];
  valueId?: string;
  onChange: (id: string) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  loading?: boolean;
  error?: string;
  noResultsText?: string;
  required?: boolean;
  /**
   * Avisa o texto digitado na caixa, para quem precisa buscar no SERVIDOR.
   *
   * O filtro embutido só alcança o que já está em [items] - ótimo para uma
   * lista fechada (categorias, planos), inútil para uma base que não cabe numa
   * página (clientes, lojas). Com este callback, quem usa o componente recarrega
   * [items] conforme a pessoa digita e o combo volta a ser um só campo, em vez de
   * exigir uma caixa de busca ao lado dele.
   *
   * Chamado apenas nas mudanças vindas da pessoa (digitar, limpar, escolher,
   * abrir), nunca no ajuste interno que o componente faz sozinho - senão o
   * chamador recarregaria a lista em resposta à própria resposta.
   *
   * Quem passa isto deve aplicar um atraso antes de ir à rede: uma requisição
   * por tecla digitada é uma tempestade que chega fora de ordem.
   */
  onQueryChange?: (query: string) => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;
  position: relative;
`;

const Label = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
`;

const Req = styled.span`
  color: ${(p) => p.theme.error};
  margin-left: 4px;
`;

const InputWrapper = styled.div<{ $disabled?: boolean; $hasError?: boolean; $isOpen?: boolean }>`
  display: flex;
  align-items: center;
  background: ${(p) => (p.$disabled ? p.theme.surfaceAlt : p.theme.surface)};
  border: 1px solid
    ${(p) => (p.$hasError ? p.theme.error : p.$isOpen ? p.theme.primary : p.theme.border)};
  border-radius: ${(p) => p.theme.radius};
  padding: 0 0.75rem;
  transition: all 0.2s;
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'text')};
  position: relative;

  &:focus-within {
    border-color: ${(p) => (p.$hasError ? p.theme.error : p.theme.primary)};
    box-shadow: 0 0 0 3px ${(p) => (p.$hasError ? p.theme.error : p.theme.primary)}22;
  }
`;

const StyledInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 0.6rem 0;
  border: none;
  background: transparent;
  font-size: 0.9rem;
  color: ${(p) => p.theme.textPrimary};
  outline: none;

  &::placeholder {
    color: ${(p) => p.theme.textMuted};
  }
  &:disabled {
    cursor: not-allowed;
  }
`;

const ItemImage = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  object-fit: cover;
  background: ${(p) => p.theme.surfaceAlt};
  border: 1px solid ${(p) => p.theme.border};
  flex-shrink: 0;
`;

const SelectedImage = styled(ItemImage)`
  width: 26px;
  height: 26px;
  margin-right: 0.5rem;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => p.theme.textMuted};
  margin-left: 0.5rem;
  flex-shrink: 0;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    color: ${(p) => p.theme.primary};
  }
`;

const DropdownMenu = styled.div`
  position: fixed;
  z-index: 99999;
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  box-shadow: ${(p) => p.theme.shadowMd};
  max-height: 240px;
  overflow-y: auto;
`;

const DropdownItem = styled.button<{ $active?: boolean }>`
  width: 100%;
  padding: 0.7rem 1rem;
  text-align: left;
  border: none;
  background: ${(p) => (p.$active ? `${p.theme.primary}15` : 'transparent')};
  color: ${(p) => (p.$active ? p.theme.primary : p.theme.textPrimary)};
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${(p) => (p.$active ? `${p.theme.primary}20` : p.theme.surfaceAlt)};
  }
`;

const DropdownItemContent = styled.span`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
`;

const DropdownItemLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NoResults = styled.div`
  padding: 1rem;
  color: ${(p) => p.theme.textMuted};
  font-size: 0.875rem;
  text-align: center;
`;

const ErrorText = styled.small`
  font-size: 0.75rem;
  color: ${(p) => p.theme.error};
`;

/** Combobox with search + dropdown, matching the Léo Delivery SearchableComboBox. */
export const SearchableComboBox: React.FC<SearchableComboBoxProps> = ({
  items,
  valueId,
  onChange,
  placeholder = 'Pesquisar...',
  label,
  disabled,
  loading,
  error,
  noResultsText = 'Nenhum resultado',
  required,
  onQueryChange,
}) => {
  const theme = useTheme();
  const [query, setQuery] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [focused, setFocused] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({ top: 0, left: 0, width: 0, maxHeight: 240 });

  /**
   * Toda mudança de texto VINDA DA PESSOA passa por aqui: guarda no estado e
   * avisa quem busca no servidor. O `setQuery` cru continua sendo usado no
   * ajuste interno logo abaixo, que não é digitação e não deve disparar busca.
   */
  const updateQuery = (value: string) => {
    setQuery(value);
    onQueryChange?.(value);
  };

  const selectedLabel = useMemo(() => (items || []).find((i) => i.id === valueId)?.label || '', [items, valueId]);
  const selectedItem = useMemo(() => (items || []).find((i) => i.id === valueId), [items, valueId]);

  useEffect(() => {
    if (!focused) setQuery('');
  }, [selectedLabel, focused]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideTrigger = !!containerRef.current && containerRef.current.contains(target);
      const insideMenu = !!menuRef.current && menuRef.current.contains(target);
      if (!insideTrigger && !insideMenu) {
        setOpen(false);
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const updatePosition = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const spaceBelow = viewportHeight - rect.bottom - 8;
      const spaceAbove = rect.top - 8;
      const openAbove = spaceBelow < 160 && spaceAbove > spaceBelow;
      const availableSpace = openAbove ? spaceAbove : spaceBelow;
      const maxHeight = Math.max(80, Math.min(240, availableSpace));
      const width = Math.min(rect.width, viewportWidth - 16);
      const left = Math.max(8, Math.min(rect.left, viewportWidth - width - 8));
      const top = openAbove ? Math.max(8, rect.top - maxHeight - 4) : rect.bottom + 4;
      setMenuStyle({ top, left, width, maxHeight });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, items.length]);

  const displayValue = focused || query ? query : selectedLabel;

  const filtered = useMemo(() => {
    const q = (query || '').toLowerCase().trim();
    const safeItems = items || [];
    if (!q) return safeItems.slice(0, 50);
    return safeItems.filter((i) => i.label.toLowerCase().includes(q)).slice(0, 50);
  }, [items, query]);

  const handleToggleOpen = () => {
    if (disabled) return;
    setOpen((prev) => {
      const next = !prev;
      setFocused(next);
      if (next) updateQuery('');
      return next;
    });
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    updateQuery('');
    if (open) setOpen(false);
    setFocused(false);
  };

  return (
    <Container ref={containerRef}>
      {label && (
        <Label>
          {label}
          {required && <Req>*</Req>}
        </Label>
      )}
      <InputWrapper $disabled={disabled} $hasError={!!error} $isOpen={open}>
        {!focused && !query && selectedItem?.imageUrl ? (
          <SelectedImage src={selectedItem.imageUrl} alt="" />
        ) : (
          !focused && !query && <Search size={18} color={theme.textMuted} style={{ marginRight: '0.5rem' }} />
        )}
        <StyledInput
          type="text"
          placeholder={placeholder}
          value={displayValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            updateQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => {
            setOpen(true);
            setFocused(true);
            updateQuery('');
          }}
          disabled={disabled}
        />
        {valueId && !disabled && (
          <IconWrapper onClick={handleClear} title="Limpar seleção">
            <X size={16} />
          </IconWrapper>
        )}
        <IconWrapper onClick={handleToggleOpen} style={{ transform: open ? 'rotate(180deg)' : 'none' }}>
          <ChevronDown size={20} />
        </IconWrapper>
      </InputWrapper>

      {error && <ErrorText>{error}</ErrorText>}

      {open &&
        !disabled &&
        typeof document !== 'undefined' &&
        createPortal(
          <DropdownMenu ref={menuRef} style={menuStyle}>
            {loading ? (
              <NoResults>Carregando...</NoResults>
            ) : filtered.length === 0 ? (
              <NoResults>{noResultsText}</NoResults>
            ) : (
              filtered.map((item) => (
                <DropdownItem
                  type="button"
                  key={item.id}
                  $active={item.id === valueId}
                  onClick={() => {
                    onChange(item.id);
                    setOpen(false);
                    setFocused(false);
                    updateQuery('');
                  }}
                >
                  <DropdownItemContent>
                    {item.imageUrl && <ItemImage src={item.imageUrl} alt="" />}
                    <DropdownItemLabel>{item.label}</DropdownItemLabel>
                  </DropdownItemContent>
                </DropdownItem>
              ))
            )}
          </DropdownMenu>,
          document.body
        )}
    </Container>
  );
};

export default SearchableComboBox;
